import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, Row, Col, Button, Space, Typography, message, Modal, Upload, Input, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faImages,
  faPlus,
  faEye,
  faEdit,
  faTrash,
  faCamera,
  faXRay,
  faNotesMedical
} from '@fortawesome/free-solid-svg-icons'
import { useSelector } from 'react-redux'

import ImageThumbnail from './components/ImageThumbnail'
import ImageUpload from './components/ImageUpload'
import ImageViewer from './components/ImageViewer'
import { createMedicalImage, readMedicalImages, updateMedicalImage, deleteMedicalImage } from './utils/api'
import { handleError } from '../../../../lib/errorHandler'
import './styles/Imaging.css'

const { Title, Text } = Typography

const Imaging = ({ bookingData, userRef, onImageUpdate }) => {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [showViewer, setShowViewer] = useState(false)
  const globalState = useSelector((state) => state)

  useEffect(() => {
    if (bookingData?._id) {
      fetchImages()
    }
  }, [bookingData])

  const fetchImages = async () => {
    setLoading(true)
    try {
      const filter = {
        bookingRef: bookingData._id,
        isActive: true
      }
      const response = await readMedicalImages(filter)
      setImages(response || [])
    } catch (e) {
      message.error('Failed to load images: ' + handleError(e, true))
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (imageData) => {
    try {
      const newImage = await createMedicalImage({
        ...imageData,
        bookingRef: bookingData._id
      })
      setImages((prev) => [...prev, newImage])
      setShowUpload(false)
      message.success('Image uploaded successfully')

      if (onImageUpdate) {
        onImageUpdate()
      }
    } catch (e) {
      message.error('Failed to upload image: ' + handleError(e, true))
    }
  }

  const handleImageClick = (image) => {
    setSelectedImage(image)
    setShowViewer(true)
  }

  const handleImageUpdate = async (imageId, updatedData) => {
    try {
      const updatedImage = await updateMedicalImage(imageId, updatedData)
      setImages((prev) => prev.map((img) => (img._id === imageId ? updatedImage : img)))
      message.success('Image updated successfully')

      if (onImageUpdate) {
        onImageUpdate()
      }
    } catch (e) {
      message.error('Failed to update image: ' + handleError(e, true))
    }
  }

  const handleImageDelete = async (imageId) => {
    try {
      await deleteMedicalImage(imageId)
      setImages((prev) => prev.filter((img) => img._id !== imageId))
      setShowViewer(false)
      message.success('Image deleted successfully')

      if (onImageUpdate) {
        onImageUpdate()
      }
    } catch (e) {
      message.error('Failed to delete image: ' + handleError(e, true))
    }
  }

  return (
    <Card
      title={
        <Space>
          <FontAwesomeIcon icon={faImages} style={{ color: '#1890ff' }} />
          <span>Medical Imaging</span>
          {images.length > 0 && (
            <Text type='secondary' style={{ fontSize: '12px', fontWeight: 'normal' }}>
              ({images.length} image{images.length > 1 ? 's' : ''})
            </Text>
          )}
        </Space>
      }
      style={{
        marginBottom: '16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
      styles={{
        header: {
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        },
        body: {
          padding: images.length > 0 ? '16px' : '0'
        }
      }}
      loading={loading}
    >
      {images.length > 0 ? (
        <div className='imaging-thumbnails-container'>
          <Row gutter={[12, 12]} align='middle'>
            {images.map((image) => (
              <Col key={image._id}>
                <ImageThumbnail image={image} onClick={() => handleImageClick(image)} />
              </Col>
            ))}
            <Col>
              <Button
                type='dashed'
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setShowUpload(true)}
                className='imaging-add-button'
                style={{
                  height: '120px',
                  width: '120px',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderColor: '#1890ff',
                  color: '#1890ff'
                }}
              >
                <div style={{ fontSize: '12px', marginTop: '8px' }}>Add Image</div>
              </Button>
            </Col>
          </Row>
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#8c8c8c'
          }}
        >
          <FontAwesomeIcon
            icon={faCamera}
            style={{
              fontSize: '48px',
              marginBottom: '16px',
              color: '#d9d9d9'
            }}
          />
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>No medical images uploaded</div>
          <div style={{ fontSize: '14px', marginBottom: '20px' }}>
            Upload X-rays, photos, scans, or other medical images
          </div>
          <Button
            type='primary'
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setShowUpload(true)}
            size='large'
          >
            Upload First Image
          </Button>
        </div>
      )}

      {/* Image Upload Modal */}
      <Modal
        title={
          <Space>
            <FontAwesomeIcon icon={faImages} />
            <span>Upload Medical Image</span>
          </Space>
        }
        open={showUpload}
        onCancel={() => setShowUpload(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <ImageUpload onUpload={handleImageUpload} onCancel={() => setShowUpload(false)} />
      </Modal>

      {/* Image Viewer Modal */}
      <Modal
        title={null}
        open={showViewer}
        onCancel={() => setShowViewer(false)}
        footer={null}
        width='90vw'
        style={{ top: 20 }}
        styles={{
          body: { padding: 0 }
        }}
        destroyOnClose
      >
        {selectedImage && (
          <ImageViewer
            image={selectedImage}
            onUpdate={(updatedData) => handleImageUpdate(selectedImage._id, updatedData)}
            onDelete={() => {
              Modal.confirm({
                title: 'Delete Image',
                content: 'Are you sure you want to delete this image? This action cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                onOk: () => handleImageDelete(selectedImage._id)
              })
            }}
            onClose={() => setShowViewer(false)}
          />
        )}
      </Modal>
    </Card>
  )
}

Imaging.propTypes = {
  bookingData: PropTypes.object.isRequired,
  userRef: PropTypes.object,
  onImageUpdate: PropTypes.func
}

export default Imaging
